import type * as PI from '@sosocio/creator-types/project';
import moment from 'moment';

/**
 * Loose view of the tag map that blueimp-load-image exposes as `data.exif`.
 *
 * Since blueimp-load-image 5 the Exif sub-directory (tag `0x8769`) is parsed
 * into a nested map instead of being flattened into the root map, so the tags
 * that hold the capture date live one level down.
 */
export interface ExifTagMap {
	[tag: number]: unknown;
}

/** EXIF pointer to the Exif sub-directory that stores capture timestamps. */
const EXIF_IFD_POINTER = 0x8769;
/** EXIF date/time when the original image data was generated. */
const EXIF_DATE_TIME_ORIGINAL = 0x9003;
/** EXIF date/time when the image was stored as digital data. */
const EXIF_DATE_TIME_DIGITIZED = 0x9004;
/** TIFF date/time of the last file change; the only date some tools keep. */
const EXIF_DATE_TIME = 0x0132;
/** EXIF stores timestamps as ASCII in this exact layout. */
const EXIF_DATE_FORMAT = 'YYYY:MM:DD HH:mm:ss';

/**
 * Read a tag from a map that may be either the nested (blueimp 5) or the
 * flattened (blueimp 2) layout.
 *
 * @param exif Root EXIF tag map.
 * @param tag Tag code to read from the Exif sub-directory.
 * @returns The raw tag value, when present.
 */
function getExifSubTag(
	exif: ExifTagMap,
	tag: number,
): unknown {
	const subDirectory = exif[EXIF_IFD_POINTER];

	if (
		subDirectory
		&& typeof subDirectory === 'object'
	) {
		return (subDirectory as ExifTagMap)[tag];
	}

	return exif[tag];
}

/**
 * Parse an EXIF ASCII timestamp into a unix timestamp in seconds.
 *
 * Cameras occasionally write blank or zeroed dates, which must not produce
 * the string `Invalid date` that would otherwise be stored and sorted on.
 *
 * @param value Raw EXIF tag value.
 * @returns Unix timestamp string, or undefined when the value is not a valid date.
 */
function parseExifDate(value: unknown): string | undefined {
	if (typeof value !== 'string') {
		return undefined;
	}

	const parsed = moment(
		value.trim(),
		EXIF_DATE_FORMAT,
		true,
	);

	return parsed.isValid()
		? parsed.format('X')
		: undefined;
}

/**
 * Resolve the capture date of a photo from its EXIF metadata.
 *
 * Prefers `DateTimeOriginal`, then `DateTimeDigitized`, then the TIFF
 * `DateTime`, matching what photo managers use to order photos.
 *
 * @param exif EXIF tag map as returned by blueimp-load-image, when present.
 * @returns Unix timestamp string in seconds, or undefined when no valid date exists.
 */
export function getExifPhotoDate(exif?: ExifTagMap | null): string | undefined {
	if (!exif) {
		return undefined;
	}

	return (
		parseExifDate(getExifSubTag(
			exif,
			EXIF_DATE_TIME_ORIGINAL,
		))
		?? parseExifDate(getExifSubTag(
			exif,
			EXIF_DATE_TIME_DIGITIZED,
		))
		?? parseExifDate(exif[EXIF_DATE_TIME])
	);
}

/**
 * Convert a stored `photodate` into a comparable number.
 *
 * Client uploads store unix seconds; photos saved by the server may carry an
 * ISO 8601 datetime instead, so both are accepted.
 *
 * @param photodate Unix timestamp or ISO 8601 datetime as stored on the photo model.
 * @returns Numeric timestamp in seconds, or undefined when the photo has no usable date.
 */
function toPhotodateNumber(photodate: PI.PhotoModel['photodate'] | undefined): number | undefined {
	if (
		photodate === null
		|| photodate === undefined
		|| photodate === ''
	) {
		return undefined;
	}

	const numericValue = Number(photodate);

	if (Number.isFinite(numericValue)) {
		return numericValue;
	}

	const parsedValue = moment(
		photodate,
		moment.ISO_8601,
		true,
	);

	return parsedValue.isValid()
		? parsedValue.valueOf() / 1000
		: undefined;
}

/**
 * Sort photos chronologically by capture date, oldest first.
 *
 * Photos without a usable date keep their relative order and are moved to the
 * end so undated files (screenshots, edited exports) do not open the book.
 *
 * @param photoModels Photo models to order.
 * @returns A new, stably sorted array.
 */
export function sortPhotosByPhotodate<T extends Pick<PI.PhotoModel, 'photodate'>>(
	photoModels: T[],
): T[] {
	return photoModels
		.map((photoModel, index) => ({
			photoModel,
			index,
			date: toPhotodateNumber(photoModel.photodate),
		}))
		.sort((a, b) => {
			if (a.date === undefined && b.date === undefined) {
				return a.index - b.index;
			}

			if (a.date === undefined) {
				return 1;
			}

			if (b.date === undefined) {
				return -1;
			}

			return (
				a.date - b.date
				|| a.index - b.index
			);
		})
		.map(({ photoModel }) => photoModel);
}
